import React, { useState } from 'react';
import axios from 'axios';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import {
  Visibility as VisibilityIcon,
  Sync as SyncIcon,
  Replay as RetryIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';

import ViewIntegrationModal from './ViewIntegrationModal';
import { appUrl } from '../../../../../utils/config';

// Material-UI styles for the integration table components
const styles = (theme) => ({
  // Base styling for status chips
  statusChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
  },
  successChip: {
    backgroundColor: '#4CA30D',
    color: '#ffffff',
  },
  failedChip: {
    backgroundColor: '#FF0000',
    color: '#ffffff',
  },
  runningChip: {
    backgroundColor: '#EC6535',
    color: '#ffffff',
  },
  retryingChip: {
    backgroundColor: '#9B1515',
    color: '#ffffff',
  },
});

const IntegrationTable = ({ classes, data, loading, onRefresh }) => {

  
  // Modal state for viewing integration details
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  // Loading state for retry operation
  const [retryLoading, setRetryLoading] = useState(false);
  // Retry result modal state
  const [retryResultModalOpen, setRetryResultModalOpen] = useState(false);
  const [retryResult, setRetryResult] = useState({ success: false, message: '', jobName: '' });

  // Generate integration status chips with color coding
  const getStatusChip = (status) => {
    let chipClasses = [classes.statusChip];
    
    // Apply appropriate styling based on status type
    switch (status.toLowerCase()) {
      case 'success':
        chipClasses.push(classes.successChip);
        break;
      case 'failed':
        chipClasses.push(classes.failedChip);
        break;
      case 'running':
        chipClasses.push(classes.runningChip);
        break;
      case 'retrying':
        chipClasses.push(classes.retryingChip);
        break;
      default:
        break;
    }
    
    return <Chip label={status} className={chipClasses.join(' ')} size="small" />;
  };

  // Process integration action button clicks
  const handleActionClick = async (action, item, event) => {
    event.stopPropagation();
    
    switch (action) {
      case 'view':
        setSelectedIntegration(item);
        setViewModalOpen(true);
        break;
      case 'sync':
        setRetryLoading(true);
        try {
          const payload = {
            jobId: item.integrationId
          };
          const response = await axios.post(`${appUrl}/api/cron/dashboard/retry`, payload);
          const apiData = response.data.data;
          
          if (apiData.success) {
            // Success
            setRetryResult({
              success: true,
              message: `${item.jobName} Retry Attempt Success`,
              jobName: item.jobName
            });
          } else {
            // Failure - use API error and statusCode directly
            setRetryResult({
              success: false,
              message: `${item.jobName} Retry Attempt Failed`,
              subMessage: apiData.error,
              errorCode: `Error Code: ${apiData.statusCode}`,
              jobName: item.jobName
            });
          }
          
          setRetryResultModalOpen(true);
       
        } catch (error) {
          console.error('API Error:', error);
          
          // Check if this is an axios error with a response (like 503, 500, etc.)
          if (error.response && error.response.data && error.response.data.data) {
            // Handle API errors that return proper response format but with error status codes
            const apiData = error.response.data.data;
            setRetryResult({
              success: false,
              message: `${item.jobName} Retry Attempt Failed`,
              subMessage: apiData.error,
              errorCode: `Error Code: ${apiData.statusCode}`,
              jobName: item.jobName
            });
          } else {
            // Handle true network errors
            setRetryResult({
              success: false,
              message: `${item.jobName} Retry Attempt Failed`,
              subMessage: 'Network error or API unavailable',
              errorCode: 'Error Code: Network Error',
              jobName: item.jobName
            });
          }
          
          setRetryResultModalOpen(true);
        } finally {
          setRetryLoading(false);
          onRefresh(1);
        }
        break;
      case 'pause':
        // Pause running integration
        break;
      default:
        break;
    }
  };

  // Closes the integration detail modal
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedIntegration(null);
  };

  // Data is already filtered and paginated by parent component

  return (
    <Grid item xs={12}>
      <TableContainer component={Paper}>
        <Table style={{ minWidth: 650 }}>
          <TableHead style={{ backgroundColor: "#F9F9FC" }}>
            <TableRow>
              <TableCell>Integration Name</TableCell>
              <TableCell>Job Name</TableCell>
              <TableCell>Vendor Plant</TableCell>
              <TableCell>Vendor Number</TableCell>
              <TableCell>Source → Target</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell align="center">Retries</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                    <Typography variant="body1">
                      Loading integrations...
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) :
            data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                    <Typography variant="body1">
                      {loading ? 'Loading integrations...' : 'No integrations found'}
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {item.integrationName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.jobName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.vendorPlant}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.vendorNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.sourceTarget}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(item.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.lastRun}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {item.retryCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                      <IconButton 
                        style={{ 
                          padding: '4px', 
                          margin: '0 2px', 
                          color: '#6c757d',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                        onClick={(e) => handleActionClick('view', item, e)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    
                    {(item.status === 'Failed' || item.status === 'failed') && (
                        <IconButton 
                          style={{ 
                            padding: '4px', 
                            margin: '0 2px', 
                            color: '#6c757d',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                          onClick={(e) => handleActionClick('sync', item, e)}
                        >
                          <SyncIcon fontSize="small" />
                        </IconButton>
                    )}
                    
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* View Integration Modal */}
      <ViewIntegrationModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        integration={selectedIntegration}
      />
      
      {/* <Backdrop
        style={{
          zIndex: 9998,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}
        open={loading}
      >
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6">Loading Integrations...</Typography>
      </Backdrop> */}
      
      {/* Retry Result Modal */}
      <Dialog
        open={retryResultModalOpen}
        onClose={() => setRetryResultModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '8px',
            padding: '20px'
          }
        }}
      >
        <DialogContent
          style={{
            textAlign: 'center',
            padding: '40px 20px 20px 20px',
            position: 'relative'
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={() => setRetryResultModalOpen(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              color: '#666'
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {/* Icon and content */}
          <div style={{ marginBottom: '20px' }}>
            {retryResult.success ? (
              <div
                style={{
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px auto'
                }}
              >
                <CheckCircleIcon style={{ color: '#4CAF50', fontSize: '60px' }} />
              </div>
            ) : (
              <div
                style={{
                  // width: '60px',
                  // height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px auto'
                }}
              >
                <CancelIcon style={{  color: '#f44336', fontSize: '60px' }} />
              </div>
            )}
            
            <Typography
              variant="h6"
              style={{
                fontWeight: 'bold',
                marginBottom: '15px',
                color: '#333'
              }}
            >
              {retryResult.message}
            </Typography>
            
            
            
            {retryResult.errorCode && (
              <Typography
                variant="body1"
                style={{
                  color: '#f44336',
                  marginBottom: '4px',
                }}
              >
                {retryResult.errorCode}
              </Typography>
            )}

            {retryResult.subMessage && (
              <Typography
                variant="body1"
                style={{
                  marginBottom: '10px',
                  color: '#f44336'
                }}
              >
                {retryResult.subMessage}
              </Typography>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Overlay Loader for Retry Operation */}
      <Backdrop
        style={{
          zIndex: 9999,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}
        open={retryLoading}
      >
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6">Retrying Integration...</Typography>
      </Backdrop>
    </Grid>
  );
};

export default withStyles(styles)(IntegrationTable);